import { useState, useEffect, useCallback } from 'react';
import { DataTable } from '../components/common/DataTable';
import { PageHeader } from '../components/common/PageHeader';
import { SearchFilter } from '../components/common/SearchFilter';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { MoreHorizontal, Eye, Download, FileText, Loader2, CheckCircle, Clock, XCircle, FileSpreadsheet, Filter, Calendar } from 'lucide-react';
import { formatDate, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Filter mode options
const FILTER_MODES = [
  { value: 'month_year', label: 'Month/Year' },
  { value: 'date_range', label: 'Date Range' },
];

// Generate month options
const MONTHS = [
  { value: 'all', label: 'All Months' },
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// Generate year options (current year and 5 years back)
const currentYear = new Date().getFullYear();
const YEARS = [
  { value: 'all', label: 'All Years' },
  ...Array.from({ length: 6 }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i),
  })),
];

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]); // For export
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('month_year');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });
      if (search) params.append('search', search);
      
      // Apply filters based on filter mode
      if (filterMode === 'month_year') {
        if (selectedMonth && selectedMonth !== 'all') params.append('month', selectedMonth);
        if (selectedYear && selectedYear !== 'all') params.append('year', selectedYear);
      } else if (filterMode === 'date_range') {
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
      }
      
      const response = await axios.get(`${API_URL}/api/invoices?${params}`);
      setInvoices(response.data.invoices || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
        totalPages: response.data.total_pages || 1,
      }));
    } catch (error) {
      toast.error('Failed to load invoices');
      setInvoices([]);
      setPagination(prev => ({ ...prev, total: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, filterMode, selectedMonth, selectedYear, startDate, endDate]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleViewInvoice = async (invoice) => {
    setShowDetailModal(true);
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API_URL}/api/invoices/${invoice.id}`);
      setSelectedInvoice(response.data);
    } catch (error) {
      toast.error('Failed to load invoice details');
      setSelectedInvoice(invoice);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDownloadPDF = (invoice) => {
    const pdfContent = generateInvoicePDF(invoice);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(pdfContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
    toast.success(`Opening ${invoice.invoice_number} for download...`);
  };

  // Export to Excel functionality
  const handleExportToExcel = async () => {
    setExporting(true);
    try {
      // Fetch all invoices for export
      const params = new URLSearchParams({ limit: '1000' });
      if (search) params.append('search', search);
      
      // Apply filters based on filter mode
      if (filterMode === 'month_year') {
        if (selectedMonth && selectedMonth !== 'all') params.append('month', selectedMonth);
        if (selectedYear && selectedYear !== 'all') params.append('year', selectedYear);
      } else if (filterMode === 'date_range') {
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
      }
      
      const response = await axios.get(`${API_URL}/api/invoices?${params}`);
      const exportData = response.data.invoices || [];
      
      if (exportData.length === 0) {
        toast.error('No invoices to export');
        return;
      }
      
      // Create CSV content
      const headers = [
        'Invoice Number',
        'Date',
        'Customer Name',
        'Phone',
        'City',
        'State',
        'Country',
        'Description',
        'Subtotal (₹)',
        'CGST (₹)',
        'SGST (₹)',
        'IGST (₹)',
        'Total (₹)',
        'Status',
        'Payment ID'
      ];
      
      const csvRows = [headers.join(',')];
      
      for (const inv of exportData) {
        const row = [
          `"${inv.invoice_number || ''}"`,
          `"${inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-IN') : ''}"`,
          `"${(inv.billing_name || inv.user_name || '').replace(/"/g, '""')}"`,
          `"${inv.user_phone || ''}"`,
          `"${(inv.billing_city || '').replace(/"/g, '""')}"`,
          `"${(inv.billing_state || '').replace(/"/g, '""')}"`,
          `"${(inv.billing_country || 'India').replace(/"/g, '""')}"`,
          `"${(inv.description || '').replace(/"/g, '""')}"`,
          (inv.subtotal || 0).toFixed(2),
          (inv.cgst || 0).toFixed(2),
          (inv.sgst || 0).toFixed(2),
          (inv.igst || 0).toFixed(2),
          (inv.total || 0).toFixed(2),
          `"${(inv.payment_status || '').toUpperCase()}"`,
          `"${inv.razorpay_payment_id || ''}"`,
        ];
        csvRows.push(row.join(','));
      }
      
      const csvContent = csvRows.join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename based on filter mode
      let filename = 'invoices';
      if (filterMode === 'month_year') {
        if (selectedYear && selectedYear !== 'all') filename += `_${selectedYear}`;
        if (selectedMonth && selectedMonth !== 'all') {
          const monthName = MONTHS.find(m => m.value === selectedMonth)?.label || selectedMonth;
          filename += `_${monthName}`;
        }
      } else if (filterMode === 'date_range' && (startDate || endDate)) {
        if (startDate) filename += `_from_${startDate}`;
        if (endDate) filename += `_to_${endDate}`;
      }
      filename += '.csv';
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${exportData.length} invoices to ${filename}`);
    } catch (error) {
      toast.error('Failed to export invoices');
    } finally {
      setExporting(false);
    }
  };

  const generateInvoicePDF = (inv) => {
    const formatAmount = (amount) => `₹${(amount || 0).toFixed(2)}`;
    const formatDateStr = (dateStr) => {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };

    return `
<!DOCTYPE html>
<html>
<head>
  <title>Invoice ${inv.invoice_number}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #333; padding: 20px; }
    .invoice-container { max-width: 800px; margin: 0 auto; border: 1px solid #ddd; padding: 30px; }
    .header { display: flex; justify-content: space-between; margin-bottom: 30px; border-bottom: 2px solid #22c55e; padding-bottom: 20px; }
    .company-info { flex: 1; }
    .company-name { font-size: 24px; font-weight: bold; color: #22c55e; margin-bottom: 5px; }
    .company-details { font-size: 11px; color: #666; line-height: 1.5; }
    .invoice-info { text-align: right; }
    .invoice-title { font-size: 28px; font-weight: bold; color: #22c55e; margin-bottom: 10px; }
    .invoice-number { font-size: 14px; font-weight: bold; }
    .invoice-date { font-size: 11px; color: #666; margin-top: 5px; }
    .billing-section { display: flex; justify-content: space-between; margin-bottom: 30px; }
    .billing-to, .payment-info { flex: 1; }
    .section-title { font-size: 12px; font-weight: bold; color: #22c55e; margin-bottom: 8px; text-transform: uppercase; }
    .billing-name { font-size: 14px; font-weight: bold; margin-bottom: 3px; }
    .billing-details { font-size: 11px; color: #666; line-height: 1.5; }
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    .items-table th { background: #22c55e; color: white; padding: 10px; text-align: left; font-size: 11px; }
    .items-table td { padding: 12px 10px; border-bottom: 1px solid #eee; }
    .items-table .amount { text-align: right; }
    .summary { margin-left: auto; width: 300px; }
    .summary-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
    .summary-row.total { border-top: 2px solid #22c55e; border-bottom: none; font-weight: bold; font-size: 16px; color: #22c55e; }
    .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: bold; text-transform: uppercase; }
    .status-paid { background: #dcfce7; color: #16a34a; }
    .status-pending { background: #fef9c3; color: #ca8a04; }
    .footer { margin-top: 40px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #eee; padding-top: 20px; }
    @media print { body { padding: 0; } .invoice-container { border: none; } }
  </style>
</head>
<body>
  <div class="invoice-container">
    <div class="header">
      <div class="company-info">
        <div class="company-name">${inv.company_name || 'Dhruthi Technologies'}</div>
        <div class="company-details">
          ${inv.company_address || '1655, 5th cross, 5th main road, Hampinagar, Vijayanagar, Bengaluru, Karnataka - 560104'}<br>
          GSTIN: ${inv.company_gstin || '29AKCPB5509R1ZS'}<br>
          Email: ${inv.company_email || 'sales@lunipin.com'}
        </div>
      </div>
      <div class="invoice-info">
        <div class="invoice-title">TAX INVOICE</div>
        <div class="invoice-number">${inv.invoice_number}</div>
        <div class="invoice-date">Date: ${formatDateStr(inv.created_at)}</div>
        <div style="margin-top: 10px;">
          <span class="status-badge ${inv.payment_status === 'paid' ? 'status-paid' : 'status-pending'}">
            ${inv.payment_status || 'PENDING'}
          </span>
        </div>
      </div>
    </div>

    <div class="billing-section">
      <div class="billing-to">
        <div class="section-title">Bill To</div>
        <div class="billing-name">${inv.billing_name || inv.user_name || 'Customer'}</div>
        <div class="billing-details">
          ${inv.user_phone || ''}<br>
          ${inv.billing_city || ''}, ${inv.billing_state || ''}<br>
          ${inv.billing_country || 'India'}
        </div>
      </div>
      <div class="payment-info">
        <div class="section-title">Payment Details</div>
        <div class="billing-details">
          Payment Date: ${formatDateStr(inv.payment_date)}<br>
          Payment ID: ${inv.razorpay_payment_id || '-'}<br>
          HSN/SAC: ${inv.hsn_sac_code || '998439'}
        </div>
      </div>
    </div>

    <table class="items-table">
      <thead>
        <tr>
          <th style="width: 50%;">Description</th>
          <th>HSN/SAC</th>
          <th>Qty</th>
          <th class="amount">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>${inv.description || 'Subscription'}</td>
          <td>${inv.hsn_sac_code || '998439'}</td>
          <td>1</td>
          <td class="amount">${formatAmount(inv.subtotal)}</td>
        </tr>
      </tbody>
    </table>

    <div class="summary">
      <div class="summary-row">
        <span>Subtotal</span>
        <span>${formatAmount(inv.subtotal)}</span>
      </div>
      ${inv.cgst > 0 ? `
      <div class="summary-row">
        <span>CGST (${inv.cgst_percent || 9}%)</span>
        <span>${formatAmount(inv.cgst)}</span>
      </div>
      <div class="summary-row">
        <span>SGST (${inv.sgst_percent || 9}%)</span>
        <span>${formatAmount(inv.sgst)}</span>
      </div>
      ` : ''}
      ${inv.igst > 0 ? `
      <div class="summary-row">
        <span>IGST (18%)</span>
        <span>${formatAmount(inv.igst)}</span>
      </div>
      ` : ''}
      <div class="summary-row total">
        <span>Total</span>
        <span>${formatAmount(inv.total)}</span>
      </div>
    </div>

    <div class="footer">
      This is a computer-generated invoice and does not require a signature.<br>
      Thank you for using Lunipin!
    </div>
  </div>
</body>
</html>
    `;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'failed':
      case 'cancelled':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-slate-700 text-slate-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return <CheckCircle className="w-3 h-3" />;
      case 'pending':
        return <Clock className="w-3 h-3" />;
      case 'failed':
      case 'cancelled':
        return <XCircle className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const columns = [
    {
      key: 'invoice',
      header: 'Invoice',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-green-500/20 flex items-center justify-center">
            <FileText className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <p className="font-medium text-white mono text-sm">{row.invoice_number}</p>
            <p className="text-xs text-slate-500">{row.billing_name || row.user_name}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (row) => (
        <div className="text-sm">
          <p className="text-slate-300">{row.billing_city || '-'}</p>
          <p className="text-xs text-slate-500">{row.billing_state || '-'}, {row.billing_country || 'India'}</p>
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (row) => (
        <div className="max-w-[200px]">
          <p className="text-slate-300 text-sm truncate">{row.description}</p>
        </div>
      ),
    },
    {
      key: 'subtotal',
      header: 'Subtotal',
      render: (row) => (
        <span className="text-slate-300 tabular-nums text-sm">{formatCurrency(row.subtotal)}</span>
      ),
    },
    {
      key: 'cgst',
      header: 'CGST',
      render: (row) => (
        <span className="text-slate-400 tabular-nums text-sm">{row.cgst > 0 ? formatCurrency(row.cgst) : '-'}</span>
      ),
    },
    {
      key: 'sgst',
      header: 'SGST',
      render: (row) => (
        <span className="text-slate-400 tabular-nums text-sm">{row.sgst > 0 ? formatCurrency(row.sgst) : '-'}</span>
      ),
    },
    {
      key: 'igst',
      header: 'IGST',
      render: (row) => (
        <span className="text-slate-400 tabular-nums text-sm">{row.igst > 0 ? formatCurrency(row.igst) : '-'}</span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      render: (row) => (
        <span className="text-green-400 font-medium tabular-nums">{formatCurrency(row.total)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(row.payment_status)}`}>
          {getStatusIcon(row.payment_status)}
          {row.payment_status?.toUpperCase() || 'PENDING'}
        </span>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      render: (row) => <span className="text-slate-400 text-sm">{formatDate(row.created_at)}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`invoice-actions-${row.id}`}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
            <DropdownMenuItem 
              onClick={() => handleViewInvoice(row)}
              className="text-slate-300 focus:bg-slate-800 focus:text-white"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Invoice
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => handleDownloadPDF(row)}
              className="text-green-400 focus:bg-green-500/10"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div data-testid="invoices-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Invoices</h1>
          <p className="text-slate-400 text-sm">View and download payment invoices from Lunipin App</p>
        </div>
        <Button 
          onClick={handleExportToExcel}
          disabled={exporting || loading}
          className="bg-green-500 hover:bg-green-600"
        >
          {exporting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <FileSpreadsheet className="w-4 h-4 mr-2" />
          )}
          Export to Excel
        </Button>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-slate-900/50 rounded-lg border border-slate-800">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-400">Filters:</span>
        </div>
        
        {/* Search */}
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by invoice number, name..."
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500"
            data-testid="invoice-search-input"
          />
        </div>
        
        {/* Filter Mode Toggle */}
        <Select value={filterMode} onValueChange={(value) => {
          setFilterMode(value);
          // Clear the other filter's values when switching
          if (value === 'month_year') {
            setStartDate('');
            setEndDate('');
          } else {
            setSelectedMonth('all');
            setSelectedYear('all');
          }
        }}>
          <SelectTrigger className="w-[140px] bg-slate-800 border-slate-700" data-testid="filter-mode-select">
            <SelectValue placeholder="Filter by" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            {FILTER_MODES.map(mode => (
              <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {filterMode === 'month_year' ? (
          <>
            {/* Month Filter */}
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700" data-testid="month-filter-select">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {MONTHS.map(month => (
                  <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Year Filter */}
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[120px] bg-slate-800 border-slate-700" data-testid="year-filter-select">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {YEARS.map(year => (
                  <SelectItem key={year.value} value={year.value}>{year.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        ) : (
          <>
            {/* Start Date */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="start-date-input"
              />
            </div>
            
            <span className="text-slate-500">to</span>
            
            {/* End Date */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="end-date-input"
              />
            </div>
          </>
        )}
        
        {/* Reset Filters */}
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => { 
            setSearch(''); 
            setSelectedMonth('all'); 
            setSelectedYear('all'); 
            setStartDate('');
            setEndDate('');
            setFilterMode('month_year');
          }}
          className="border-slate-700 text-slate-400 hover:text-white"
          data-testid="reset-filters-btn"
        >
          Reset
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={invoices}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        emptyMessage="No invoices found"
      />

      {/* Invoice Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-400" />
              Invoice Details
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-500" />
            </div>
          ) : selectedInvoice && (
            <div className="space-y-6">
              {/* Invoice Header */}
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedInvoice.invoice_number}</h3>
                  <p className="text-slate-400">{formatDate(selectedInvoice.created_at)}</p>
                </div>
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedInvoice.payment_status)}`}>
                  {getStatusIcon(selectedInvoice.payment_status)}
                  {selectedInvoice.payment_status?.toUpperCase() || 'PENDING'}
                </span>
              </div>

              {/* Customer Info */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-green-400 mb-2">Customer</h4>
                <p className="text-white font-medium">{selectedInvoice.billing_name || selectedInvoice.user_name}</p>
                <p className="text-slate-400 text-sm">{selectedInvoice.user_phone}</p>
                <p className="text-slate-400 text-sm">
                  {selectedInvoice.billing_city}, {selectedInvoice.billing_state}, {selectedInvoice.billing_country || 'India'}
                </p>
              </div>

              {/* Description */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-green-400 mb-2">Description</h4>
                <p className="text-white">{selectedInvoice.description}</p>
              </div>

              {/* Amount Breakdown */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-green-400 mb-3">Amount Breakdown</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Subtotal</span>
                    <span className="text-white">{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                  {selectedInvoice.cgst > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-400">CGST ({selectedInvoice.cgst_percent || 9}%)</span>
                        <span className="text-white">{formatCurrency(selectedInvoice.cgst)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">SGST ({selectedInvoice.sgst_percent || 9}%)</span>
                        <span className="text-white">{formatCurrency(selectedInvoice.sgst)}</span>
                      </div>
                    </>
                  )}
                  {selectedInvoice.igst > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">IGST (18%)</span>
                      <span className="text-white">{formatCurrency(selectedInvoice.igst)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-slate-700">
                    <span className="text-white font-medium">Total</span>
                    <span className="text-green-400 font-bold text-lg">{formatCurrency(selectedInvoice.total)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-green-400 mb-2">Payment Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">Payment Date</p>
                    <p className="text-white">{formatDate(selectedInvoice.payment_date)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Payment ID</p>
                    <p className="text-white font-mono text-xs">{selectedInvoice.razorpay_payment_id || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Download Button */}
              <Button 
                onClick={() => handleDownloadPDF(selectedInvoice)}
                className="w-full bg-green-500 hover:bg-green-600"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Invoice PDF
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
// Last updated: Mon Jan 19 18:42:16 UTC 2026
